// frontend/src/components/PostProduction/SalesIntelligencePanel.tsx
import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningIcon from '@mui/icons-material/Warning';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { PerformanceIntelligence } from '../../types/PPDash';

interface SalesIntelligencePanelProps {
  performances: PerformanceIntelligence[];
}

interface SalesPattern {
  finalWeekPercentage: number;
  final48HoursPercentage: number;
  openingWeekPercentage: number;
  midSaleWeakness: boolean; // Weeks 4-6
  averageSalesPerWeek: { [key: string]: number };
  lastMinuteDependency: 'critical' | 'high' | 'moderate' | 'healthy';
}

interface CapacityBracket {
  bracket: string;
  count: number;
  avgFinalWeekSales: number;
  avgFinal48HoursSales: number;
  finalCapacity: number;
}

const SalesIntelligencePanel: React.FC<SalesIntelligencePanelProps> = ({ performances }) => {
  
  const analysis = useMemo(() => {
    // Filter performances with sales velocity data
    const perfWithVelocity = performances.filter(p => 
      p.salesVelocity && p.salesWindow?.duration
    );

    if (perfWithVelocity.length === 0) {
      return null;
    }

    // Calculate overall sales pattern
    let totalFinalWeekSales = 0;
    let totalFinal48HoursSales = 0;
    let totalOpeningWeekSales = 0;
    let totalAllSales = 0;
    const weeklyTotals: { [key: string]: number } = {};

    perfWithVelocity.forEach(perf => {
      const velocity = perf.salesVelocity!;
      const duration = perf.salesWindow!.duration;

      for (let i = 1; i <= duration; i++) {
        const weekKey = `week${i}`;
        const weekSales = typeof velocity[weekKey] === 'number' ? velocity[weekKey] as number : 0;
        
        totalAllSales += weekSales;
        weeklyTotals[weekKey] = (weeklyTotals[weekKey] || 0) + weekSales;

        if (i === 1) totalFinalWeekSales += weekSales;
        if (i === duration) totalOpeningWeekSales += weekSales;
      }

      // Estimate final 48 hours (roughly 30% of final week based on industry standards)
      totalFinal48HoursSales += (typeof velocity.week1 === 'number' ? velocity.week1 as number : 0) * 0.3;
    });

    const finalWeekPct = (totalFinalWeekSales / totalAllSales) * 100;
    const final48HrsPct = (totalFinal48HoursSales / totalAllSales) * 100;
    const openingWeekPct = (totalOpeningWeekSales / totalAllSales) * 100;

    // Check for mid-sale weakness (weeks 4-6)
    const midSaleWeeks = ['week4', 'week5', 'week6'];
    const midSaleSales = midSaleWeeks.reduce((sum, week) => sum + (weeklyTotals[week] || 0), 0);
    const midSalePct = (midSaleSales / totalAllSales) * 100;
    const midSaleWeak = midSalePct < 15; // Less than 15% of sales in mid-period is weak

    // Determine dependency level
    let dependency: 'critical' | 'high' | 'moderate' | 'healthy';
    if (finalWeekPct > 50) dependency = 'critical';
    else if (finalWeekPct > 40) dependency = 'high';
    else if (finalWeekPct > 30) dependency = 'moderate';
    else dependency = 'healthy';

    // Average sales per week
    const avgSalesPerWeek: { [key: string]: number } = {};
    Object.keys(weeklyTotals).forEach(week => {
      avgSalesPerWeek[week] = weeklyTotals[week] / perfWithVelocity.length;
    });

    const pattern: SalesPattern = {
      finalWeekPercentage: finalWeekPct,
      final48HoursPercentage: final48HrsPct,
      openingWeekPercentage: openingWeekPct,
      midSaleWeakness: midSaleWeak,
      averageSalesPerWeek: avgSalesPerWeek,
      lastMinuteDependency: dependency
    };

    // Capacity bracket analysis
    const brackets = [
      { min: 0, max: 30, label: '<30% capacity' },
      { min: 30, max: 50, label: '30-50% capacity' },
      { min: 50, max: 70, label: '50-70% capacity' },
      { min: 70, max: 90, label: '70-90% capacity' },
      { min: 90, max: 100, label: '90%+ capacity' }
    ];

    const capacityBrackets: CapacityBracket[] = brackets.map(bracket => {
      const showsInBracket = perfWithVelocity.filter(p => {
        const cap = p.attendance.capacity || 0;
        return cap >= bracket.min && cap < bracket.max;
      });

      if (showsInBracket.length === 0) {
        return {
          bracket: bracket.label,
          count: 0,
          avgFinalWeekSales: 0,
          avgFinal48HoursSales: 0,
          finalCapacity: 0
        };
      }

      const avgFinalWeek = showsInBracket.reduce((sum, p) => {
        const week1Sales = typeof p.salesVelocity!.week1 === 'number' ? 
          p.salesVelocity!.week1 as number : 0;
        return sum + week1Sales;
      }, 0) / showsInBracket.length;

      const avgFinal48 = avgFinalWeek * 0.3; // Estimate

      const avgCapacity = showsInBracket.reduce((sum, p) => 
        sum + (p.attendance.capacity || 0), 0) / showsInBracket.length;

      return {
        bracket: bracket.label,
        count: showsInBracket.length,
        avgFinalWeekSales: Math.round(avgFinalWeek),
        avgFinal48HoursSales: Math.round(avgFinal48),
        finalCapacity: Math.round(avgCapacity)
      };
    });

    // Artist performance (top 10 by capacity)
    const artistPerformance: { 
      name: string; 
      genre: string; 
      capacity: number; 
      openingWeekSales: number;
      finalCapacity: number;
    }[] = [];
    
    perfWithVelocity.forEach(p => {
      const duration = p.salesWindow!.duration;
      const openingWeekKey = `week${duration}`;
      const openingSales = typeof p.salesVelocity![openingWeekKey] === 'number' ? 
        p.salesVelocity![openingWeekKey] as number : 0;
      
      artistPerformance.push({
        name: p.name,
        genre: p.genre || 'Unspecified',
        capacity: p.attendance.capacity || 0,
        openingWeekSales: openingSales,
        finalCapacity: p.attendance.capacity || 0
      });
    });

    // Sort by capacity (best performers first)
    artistPerformance.sort((a, b) => b.capacity - a.capacity);
    const topPerformers = artistPerformance.slice(0, 10);

    return {
      pattern,
      capacityBrackets: capacityBrackets.filter(b => b.count > 0),
      topPerformers,
      totalShows: perfWithVelocity.length
    };

  }, [performances]);

  if (!analysis) {
    return (
      <Paper sx={{ p: 3, backgroundColor: 'background.paper' }}>
        <Typography color="text.secondary">
          No sales velocity data available for analysis
        </Typography>
      </Paper>
    );
  }

  const { pattern, capacityBrackets, topPerformers, totalShows } = analysis;

  const getDependencyColor = (dep: string) => {
    switch (dep) {
      case 'critical': return 'error';
      case 'high': return 'warning';
      case 'moderate': return 'info';
      case 'healthy': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sales Intelligence: Historical Patterns & Insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Based on {totalShows} past performance{totalShows !== 1 ? 's' : ''} with complete sales data
      </Typography>

      {/* Sales Pattern Overview */}
      <Alert 
        severity="info"
        sx={{ mb: 3 }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          Natural Sales Pattern Observed
        </Typography>
        <Typography variant="body2">
          {pattern.finalWeekPercentage.toFixed(1)}% of ticket sales happen in the final week, 
          with approximately {pattern.final48HoursPercentage.toFixed(1)}% in the last 48 hours. 
          This is typical for live events - people tend to buy closer to showtime. The key is understanding this pattern for better planning.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ backgroundColor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">When People Buy</Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Opening Week</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {pattern.openingWeekPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of total sales
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">Final Week</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                  {pattern.finalWeekPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of total sales
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">Final 48 Hours (est.)</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {pattern.final48HoursPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  of total sales
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                Understanding this pattern helps with staffing, marketing timing, and capacity predictions
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Capacity Bracket Predictions */}
        <Grid item xs={12} md={8}>
          <Card sx={{ backgroundColor: 'background.paper', height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Predictive Intelligence by Capacity</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                What to expect in final week based on current trajectory
              </Typography>

              <Grid container spacing={2}>
                {capacityBrackets.map((bracket, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {bracket.bracket}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {bracket.count} show{bracket.count !== 1 ? 's' : ''} in dataset
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Final Week: <strong>{bracket.avgFinalWeekSales} tickets</strong>
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        Final 48hrs: <strong>~{bracket.avgFinal48HoursSales} tickets</strong>
                      </Typography>
                      <Typography variant="body2">
                        Ended at: <strong>{bracket.finalCapacity}% capacity</strong>
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Insight:</strong> Shows at 50% capacity 7 days out historically add {capacityBrackets.find(b => b.bracket.includes('50-70'))?.avgFinalWeekSales || 0} tickets in the final week. 
                  Use this to predict final attendance and adjust marketing spend accordingly.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendations for Stakeholders */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LightbulbIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Strategic Recommendations by Role</Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Marketing */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    For Marketing Team
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CalendarTodayIcon fontSize="small" color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Opening week performance"
                        secondary={`${pattern.openingWeekPercentage.toFixed(0)}% of sales happen at launch. Consider testing different announcement timing to see if this can be improved.`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUpIcon fontSize="small" color="info" /></ListItemIcon>
                      <ListItemText 
                        primary="Mid-window opportunity"
                        secondary={`Weeks 3-6 are quieter. Consider reminder campaigns or artist interviews to maintain momentum during this period.`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LightbulbIcon fontSize="small" color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Final week is reliable"
                        secondary={`${pattern.finalWeekPercentage.toFixed(0)}% buy final week naturally. Focus early-window budget on awareness building rather than last-minute pushing.`}
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* GM & Operations */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    For General Manager
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CalendarTodayIcon fontSize="small" /></ListItemIcon>
                      <ListItemText 
                        primary="Staffing window"
                        secondary="Most sales finalize in final week, so plan staffing decisions with this timing in mind. Leave flexibility where possible."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUpIcon fontSize="small" color="info" /></ListItemIcon>
                      <ListItemText 
                        primary="Use capacity brackets for forecasting"
                        secondary="Show at 60% with 7 days out? Check the predictions below to estimate final attendance rather than guessing."
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LightbulbIcon fontSize="small" color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Pattern awareness"
                        secondary="Knowing the typical sales curve helps distinguish underperforming shows from those just following the normal pattern."
                      />
                    </ListItem>
                  </List>
                </Grid>

                {/* Board & Director */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                    For Board & Director
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><CalendarTodayIcon fontSize="small" color="primary" /></ListItemIcon>
                      <ListItemText 
                        primary="Sales timing is industry-standard"
                        secondary={`${pattern.finalWeekPercentage.toFixed(0)}% final-week sales is typical for live events. People book closer to the date. Understanding this pattern helps with cash flow planning and marketing budget allocation.`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><TrendingUpIcon fontSize="small" color="info" /></ListItemIcon>
                      <ListItemText 
                        primary="Predictive intelligence available"
                        secondary={`Historical patterns show predictable outcomes. Shows at 50% capacity 7 days out typically end at ${capacityBrackets.find(b => b.bracket.includes('50-70'))?.finalCapacity || 65}%. This helps avoid panic decisions or unnecessary discounting.`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon><LightbulbIcon fontSize="small" color="success" /></ListItemIcon>
                      <ListItemText 
                        primary="Data-driven decision making"
                        secondary="Use these patterns to inform: (1) Marketing budget timing, (2) Staffing schedules, (3) When to intervene vs let shows run naturally, (4) Genre and artist booking decisions."
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Performers */}
        <Grid item xs={12}>
          <Card sx={{ backgroundColor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Shows
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ranked by final capacity. Artist names help identify whether genre or specific artist drove success.
              </Typography>
              <Grid container spacing={2}>
                {topPerformers.map((artist, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper sx={{ p: 2, backgroundColor: 'action.hover' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          #{index + 1}
                        </Typography>
                        <Chip 
                          label={`${artist.capacity}%`} 
                          color={artist.capacity >= 90 ? 'success' : artist.capacity >= 70 ? 'info' : 'default'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {artist.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        Genre: {artist.genre}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Opening week: {artist.openingWeekSales} tickets
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Use this to assess:</strong> Is the genre consistently strong, or was it specific visiting artists? 
                  Are local artists performing differently than international? This context helps booking decisions.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SalesIntelligencePanel;